/**
 * REGISTRE DES ÉTAPES DU SUIVI DE PRIVATISATION.
 *
 * Un seul endroit pour les libellés et l'ordre, sur le modèle de `collecteStatus.ts` — jamais de libellé
 * recopié dans un composant, sinon deux copies finissent par diverger.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────────
 * LE DOSSIER N'EST PAS UNE PRIVATISATION. C'est la correction de fond du 2026-08-26, apportée par Emeni :
 * « avant il fallait rattacher le projet à une privatisation déjà existante, alors que non, justement la
 * privatisation n'existe pas encore — mais pour qu'elle existe on doit passer certaines étapes ».
 *
 * Le dossier naît d'un coup de téléphone : un nom, un email, un numéro, particulier ou professionnel, et
 * rien d'autre. Ni date, ni nombre de couverts — ils se récoltent au rendez-vous et dans les échanges qui
 * suivent. La privatisation, elle, n'est créée dans Événementiel qu'À LA SIGNATURE DU DEVIS : c'est le
 * moment où l'on est sûr qu'elle aura lieu, et donc le seul où elle mérite une place dans le calendrier.
 *
 * Conséquence assumée : le dossier vit dans sa propre table, pas sur `evenementiel`. Couper le module
 * rendrait donc les affaires en cours invisibles — c'est pour ça que la coupure est refusée tant qu'il
 * reste un dossier ouvert.
 * ─────────────────────────────────────────────────────────────────────────────────────────────────────
 */

/** Les étapes du parcours nominal, dans l'ordre où on les traverse. */
export const PIPELINE_STAGE_ORDER = [
    'demande_recue',
    'plaquette_envoyee',
    'retour_client',
    'brief_collecte',
    'devis_envoye',
    'devis_signe',
    'privatisation_creee',
    'prestataires_bookes',
    'fournisseurs_bookes',
    'collecte_menus',
    'jour_j',
    'cloturee',
] as const;

/** Les issues qui interrompent le parcours. Elles n'ont pas de rang : on n'en revient pas tout seul. */
export const PIPELINE_STAGE_TERMINAL = ['perdue', 'annulee'] as const;

export type PipelineStage =
    | (typeof PIPELINE_STAGE_ORDER)[number]
    | (typeof PIPELINE_STAGE_TERMINAL)[number];

export const PIPELINE_STAGE_LABELS: Record<PipelineStage, string> = {
    demande_recue: 'Demande reçue',
    plaquette_envoyee: 'Plaquette envoyée',
    retour_client: 'Retour client',
    brief_collecte: 'Brief collecté',
    devis_envoye: 'Devis envoyé',
    devis_signe: 'Devis signé',
    privatisation_creee: 'Privatisation créée',
    prestataires_bookes: 'Prestataires bookés',
    fournisseurs_bookes: 'Fournisseurs bookés',
    collecte_menus: 'Collecte des menus',
    jour_j: 'Jour J',
    cloturee: 'Clôturée',
    perdue: 'Perdue',
    annulee: 'Annulée',
};

/**
 * Ce qu'il reste à faire quand un dossier est à cette étape — c'est ce libellé, et non le nom de l'étape,
 * qui s'affiche sur les compteurs cliquables du tableau de bord : on y cherche du travail en attente, pas
 * un état de fait. « 3 devis à établir » se lit tout seul ; « 3 briefs collectés » demande un effort.
 */
export const PIPELINE_STAGE_PENDING_LABELS: Record<PipelineStage, string> = {
    demande_recue: 'Plaquette à envoyer',
    plaquette_envoyee: 'En attente du client',
    retour_client: 'Brief à collecter',
    brief_collecte: 'Devis à établir',
    devis_envoye: 'En attente de signature',
    devis_signe: 'Privatisation à créer',
    privatisation_creee: 'Prestataires à booker',
    prestataires_bookes: 'Fournisseurs à booker',
    fournisseurs_bookes: 'Menus à lancer',
    collecte_menus: 'Collecte en cours',
    jour_j: 'Service à assurer',
    cloturee: 'Clôturée',
    perdue: 'Perdue',
    annulee: 'Annulée',
};

/**
 * LE JALON PIVOT. Rien ne démarre avant lui : ni la privatisation dans Événementiel, ni les prestataires,
 * ni les fournisseurs, ni les menus. C'est le moment où l'on est sûr que l'événement aura lieu.
 */
export const PIPELINE_STAGE_PIVOT: PipelineStage = 'devis_signe';

/**
 * Étapes que l'on peut sauter : toutes les privatisations n'ont pas besoin d'extras ni de commande
 * fournisseur. Les sauter avance le dossier sans mentir sur ce qui a été fait — le journal garde la trace
 * qu'elles ont été passées, pas accomplies.
 */
export const PIPELINE_STAGE_SKIPPABLE: PipelineStage[] = ['privatisation_creee', 'prestataires_bookes'];

export const isTerminalStage = (stage?: string | null): boolean =>
    !!stage && (PIPELINE_STAGE_TERMINAL as readonly string[]).includes(stage);

export const isKnownStage = (stage?: string | null): stage is PipelineStage =>
    !!stage && stage in PIPELINE_STAGE_LABELS;

/** Rang dans le parcours nominal ; -1 pour une issue terminale ou une étape inconnue. */
export const stageRank = (stage?: string | null): number =>
    (PIPELINE_STAGE_ORDER as readonly string[]).indexOf(String(stage || ''));

/** Vrai dès que le devis signé est arrivé — seule cette réponse ouvre la suite du parcours. */
export const hasPassedPivot = (stage?: string | null): boolean =>
    stageRank(stage) >= stageRank(PIPELINE_STAGE_PIVOT);
